import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card
} from "react-bootstrap";
import axiosInstance from "../../api/axiosInstance";
import colors from "../../constants/colors";
import EditableWrapper from "../../components/EditableWrapper/EditableWrapper";
import { API_BASE_URL } from "../../config/api";
import { Link } from "react-router-dom";

const AboutPage = () => {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [navVisible, setNavVisible] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const isScrolling = useRef(false);
  const totalSections = 3;
  const pageRef = useRef(null);

  // Show navigation on mouse movement, hide after inactivity
  useEffect(() => {
    axiosInstance.get("/product").then((response) => {
      if (response && response.rows) {
        // Mapear os produtos para o formato correto
        const formattedProducts = response.rows.slice(0, 3).map(product => ({
          id: product.id,
          name: product.name,
          description: product.description,
          images: product.images || []
        }));
        setProducts(formattedProducts);
      }
    }).catch((err) => {
      console.error("Error fetching products:", err);
    });

    axiosInstance.get("/service").then((response) => {
      if (response) {
        setServices(response.slice(0, 3));
      }
    }).catch((err) => {
      console.error("Error fetching services:", err);
    });
    
    const handleMouseMove = () => {
      setNavVisible(true);
      clearTimeout(window.navTimeout);
      window.navTimeout = setTimeout(() => setNavVisible(false), 3000);
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      clearTimeout(window.navTimeout);
    };
  }, []);

  // Enable keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        handleNavigation(1);
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        handleNavigation(-1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentSection]);

  const handleNavigation = (direction) => {
    setCurrentSection((prev) => {
      const newIndex = prev + direction;
      return newIndex < 0
        ? 0
        : newIndex >= totalSections
        ? totalSections - 1
        : newIndex;
    });
  };

  const handleWheel = (e) => {
    if (isScrolling.current) return;
    isScrolling.current = true;
    if (e.deltaY > 0 && currentSection < totalSections - 1) {
      handleNavigation(1);
    } else if (e.deltaY < 0 && currentSection > 0) {
      handleNavigation(-1);
    }
    setTimeout(() => (isScrolling.current = false), 800);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isSignificant = Math.abs(distance) > 50;

    if (isSignificant && !isScrolling.current) {
      isScrolling.current = true;
      if (distance > 0 && currentSection < totalSections - 1) {
        // Swipe up
        handleNavigation(1);
      } else if (distance < 0 && currentSection > 0) {
        // Swipe down
        handleNavigation(-1);
      }
      setTimeout(() => (isScrolling.current = false), 800);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Get transition direction for animation
  const getTransitionStyles = (index) => {
    const isActive = currentSection === index;
    const wasActive =
      currentSection === index + 1 || currentSection === index - 1;
    const direction = currentSection > index ? -1 : 1;

    return {
      position: "absolute",
      width: "100%",
      height: "100vh",
      left: 0,
      backgroundColor: colors.secondary,
      transition: "opacity 1.2s ease-in-out, transform 1.2s ease-in-out",
      opacity: isActive ? 1 : wasActive ? 0 : 0,
      transform: isActive ? "translateY(0)" : `translateY(${direction * 10}vh)`,
      zIndex: isActive ? 2 : wasActive ? 1 : 0,
    };
  };

  // Section Components
  const HeroSection = (
    <div style={getTransitionStyles(0)}>
      <Container fluid className="h-100">
        <Row className="h-100 align-items-center">
          <Col
            md={6}
            className="h-100 d-flex align-items-center justify-content-center p-4"
          >
            <div
              className="position-relative"
              style={{ width: "100%", maxHeight: "80vh", overflow: "hidden" }}
            >
              <img
                src= {`${API_BASE_URL}/image/Sobre`}
                alt="Hero"
                className="img-fluid shadow"
                style={{
                  width: "100%",
                  objectFit: "cover",
                  borderRadius: "12px",
                  transition: "transform 0.5s ease",
                  transform: currentSection === 0 ? "scale(1)" : "scale(0.95)",
                }}
                crossorigin="anonymous"
              />
              <div
                className="position-absolute"
                style={{
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 50%)",
                  borderRadius: "12px",
                }}
              />
            </div>
          </Col>
          <Col xs={12} md={6} className="h-100 d-flex align-items-center p-4">
            <div
              className="w-100"
              style={{
                opacity: currentSection === 0 ? 1 : 0,
                transform:
                  currentSection === 0 ? "translateY(0)" : "translateY(30px)",
                transition: "opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s",
              }}
            >
              <EditableWrapper
                section="hero-content"
                style={{
                  color: colors.light,
                  fontSize: "1.2rem",
                  marginTop: "1.5rem",
                  background: "transparent"
                }}
              >
                Transformamos sua presença online em uma experiência memorável e
                autêntica. Descubra como podemos ajudar sua marca a alcançar
                novos patamares.
                <Button
                  size="lg"
                  style={{
                    backgroundColor: colors.secondary,
                    borderColor: colors.secondary,
                    color: colors.light,
                    marginTop: "2rem",
                    borderRadius: "30px",
                    padding: "0.75rem 2rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.transform = "translateY(-3px)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.transform = "translateY(0)")
                  }
                >
                  Conheça Nosso Trabalho
                </Button>
              </EditableWrapper>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );

  const ProductsSection = (
    <div style={getTransitionStyles(1)}>
      <Container fluid className="h-100">
        <Row className="h-100 align-items-center">
          <Col className="text-center p-4">
            <h2
              style={{
                color: colors.light,
                marginBottom: "3rem",
                opacity: currentSection === 1 ? 1 : 0,
                transform:
                  currentSection === 1 ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
              }}
            >
              Nossos Produtos
            </h2>
            <Row xs={1} sm={2} md={3} className="g-4 justify-content-center">
              {products && products.length > 0 ? (
                products.map((product, index) => (
                  <Col key={product.id}>
                    <Card
                      className="h-100 border-0 shadow-sm"
                      style={{
                        background: `linear-gradient(145deg, ${colors.primary} 0%, ${colors.light} 100%)`,
                        borderRadius: "15px",
                        overflow: "hidden",
                        opacity: currentSection === 1 ? 1 : 0,
                        transform:
                          currentSection === 1
                            ? "translateY(0)"
                            : "translateY(30px)",
                        transition: `opacity 0.8s ease ${
                          0.3 + index * 0.2
                        }s, transform 0.8s ease ${0.3 + index * 0.2}s`,
                      }}
                    >
                      <div style={{ height: "200px", overflow: "hidden" }}>
                        {product.images && product.images[0] ? (
                          <Card.Img
                            variant="top"
                            src={product.images[0].src}
                            alt={product.name}
                            style={{
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.5s ease",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.transform = "scale(1.05)")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.transform = "scale(1)")
                            }
                            onError={(e) => {
                              e.target.onerror = null;
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f8f9fa"
                            }}
                          >
                            <span>Imagem não disponível</span>
                          </div>
                        )}
                      </div>
                      <Card.Body>
                        <Card.Title style={{ color: colors.dark }}>
                          {product.name}
                        </Card.Title>
                        <Card.Text style={{ color: colors.textGray }}>
                          {product.description}
                        </Card.Text>
                        <Button
                          as={Link}
                          to={`/produtos/${product.id}`}
                          variant="outline-primary"
                          style={{
                            borderRadius: "30px",
                            borderColor: colors.secondary,
                            color: colors.secondary,
                            transition: "all 0.3s ease",
                          }}
                          size="sm"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = colors.secondary;
                            e.target.style.color = colors.light;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            e.target.style.color = colors.secondary;
                          }}
                        >
                          Mais Detalhes
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col className="text-center">
                  <p style={{ color: colors.light }}>Nenhum produto disponível no momento.</p>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );

  const ServicesSection = (
    <div style={getTransitionStyles(2)}>
      <Container fluid className="h-100">
        <Row className="h-100 align-items-center">
          <Col className="text-center p-4">
            <h2
              style={{
                color: colors.light,
                marginBottom: "3rem",
                opacity: currentSection === 2 ? 1 : 0,
                transform:
                  currentSection === 2 ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
              }}
            >
              Nossos Serviços
            </h2>
            <Row xs={1} sm={2} md={3} className="g-4 justify-content-center">
              {services && services.length > 0 ? (
                services.map((service, index) => (
                  <Col key={service.id}>
                    <Card
                      className="h-100 border-0 shadow-sm"
                      style={{
                        background: `linear-gradient(145deg, ${colors.primary} 0%, ${colors.light} 100%)`,
                        borderRadius: "15px",
                        overflow: "hidden",
                        opacity: currentSection === 2 ? 1 : 0,
                        transform:
                          currentSection === 2
                            ? "translateY(0)"
                            : "translateY(30px)",
                        transition: `opacity 0.8s ease ${
                          0.3 + index * 0.2
                        }s, transform 0.8s ease ${0.3 + index * 0.2}s`,
                      }}
                    >
                      <div style={{ height: "200px", overflow: "hidden" }}>
                        {service.image ? (
                          <Card.Img
                            variant="top"
                            src={service.image}
                            alt={service.name}
                            style={{
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.5s ease",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.transform = "scale(1.05)")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.transform = "scale(1)")
                            }
                            onError={(e) => {
                              e.target.onerror = null;
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f8f9fa"
                            }}
                          >
                            <span>Imagem não disponível</span>
                          </div>
                        )}
                      </div>
                      <Card.Body>
                        <Card.Title style={{ color: colors.dark }}>
                          {service.name}
                        </Card.Title>
                        <Card.Text style={{ color: colors.textGray }}>
                          {service.short_description}
                        </Card.Text>
                        <Button
                          as="a"
                          href={service.schedule_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outline-primary"
                          style={{
                            borderRadius: "30px",
                            borderColor: colors.secondary,
                            color: colors.secondary,
                            transition: "all 0.3s ease",
                          }}
                          size="sm"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = colors.secondary;
                            e.target.style.color = colors.light;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            e.target.style.color = colors.secondary;
                          }}
                        >
                          Agendar
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col className="text-center">
                  <p style={{ color: colors.light }}>Nenhum serviço disponível no momento.</p>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );

  // Section Indicator/Navigation Dots
  const sectionDots = (
    <div
      className="section-indicators"
      style={{
        position: "fixed",
        right: "20px",
        top: "50%",
        transform: "translateY(-50%)",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        zIndex: 100,
        opacity: navVisible ? 1 : 0.3,
        transition: "opacity 0.3s ease",
      }}
    >
      {[...Array(totalSections)].map((_, index) => (
        <button
          key={index}
          onClick={() => setCurrentSection(index)}
          aria-label={`Go to section ${index + 1}`}
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            border: "none",
            backgroundColor:
              currentSection === index ? colors.secondary : colors.light,
            opacity: currentSection === index ? 1 : 0.7,
            cursor: "pointer",
            transition: "all 0.3s ease",
            transform: currentSection === index ? "scale(1.2)" : "scale(1)",
          }}
        />
      ))}
    </div>
  );

  return (
    <div
      ref={pageRef}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ overflow: "hidden", height: "100vh", position: "relative" }}
      tabIndex={0}
    >
      {HeroSection}
      {ProductsSection}
      {ServicesSection}

      {/* Section Indicators */}
      {sectionDots}

      {/* Navigation Arrows */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          zIndex: 100,
          opacity: navVisible ? 1 : 0,
          transition: "opacity 0.3s ease",
          pointerEvents: navVisible ? "auto" : "none",
        }}
      >
        {currentSection > 0 && (
          <Button
            variant="light"
            onClick={() => handleNavigation(-1)}
            style={{
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              fontSize: "1.5rem",
            }}
            aria-label="Previous section"
          >
            ↑
          </Button>
        )}
        {currentSection < totalSections - 1 && (
          <Button
            variant="light"
            onClick={() => handleNavigation(1)}
            style={{
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              fontSize: "1.5rem",
            }}
            aria-label="Next section"
          >
            ↓
          </Button>
        )}
      </div>

      {/* Scroll indicator for first time visitors */}
      {currentSection === 0 && (
        <div
          className="scroll-indicator"
          style={{
            position: "absolute",
            bottom: "40px",
            left: "50%",
            transform: "translateX(-50%)",
            color: colors.light,
            textAlign: "center",
            animation: "fadeInOut 2s infinite",
            opacity: 0.8,
          }}
        >
          <div style={{ fontSize: "1.5rem", marginBottom: "5px" }}>↓</div>
          <div style={{ fontSize: "0.8rem" }}>Deslize para explorar</div>
        </div>
      )}

      {/* Add global animation keyframes */}
      <style>{`
        @keyframes fadeInOut {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(10px); }
        }
        
        .instagram-image-wrapper:hover .instagram-overlay {
          opacity: 1;
        }
        
        @media (max-width: 768px) {
          .section-indicators {
            right: 10px;
          }
          
          .section-indicators button {
            width: 10px;
            height: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;
